import { useState, useEffect, useCallback, useRef } from 'react';
import { getChatKey, getSendTarget } from '../utils';

const POLL_INTERVAL = 7000;
const PAGE_SIZE = 50;

/**
 * Mensagens da conversa aberta:
 *  - carga inicial paginada (últimas 50)
 *  - polling incremental (sinceId) — mensagens novas aparecem sem reabrir o chat
 *  - loadOlder (beforeId) — scroll infinito para cima
 *  - envio otimista com status por mensagem: sending | sent | failed (+ retry)
 *
 * Convenção do backend: senderPhone da tabela é sempre o telefone do paciente.
 */
export default function useChatMessages({
  chat,
  GetWhatsappChatMessages,
  SendWhatsappMessage,
  SetWhatsappChatRead,
  onChatRead,
  onPreviewUpdate,
}) {
  // Identidade da conversa (chatLid estável) ≠ alvo de envio (telefone
  // real quando conhecido, senão o próprio lid — a Z-API aceita os dois)
  const chatKey = getChatKey(chat);
  const sendTarget = getSendTarget(chat);
  const sendTargetRef = useRef(sendTarget);
  sendTargetRef.current = sendTarget;

  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [hasMore, setHasMore] = useState(false);

  // Refs para evitar closures velhas dentro do interval de polling
  const messagesRef = useRef(messages);
  messagesRef.current = messages;

  const apiRef = useRef({ GetWhatsappChatMessages, SendWhatsappMessage, SetWhatsappChatRead, onChatRead, onPreviewUpdate });
  apiRef.current = { GetWhatsappChatMessages, SendWhatsappMessage, SetWhatsappChatRead, onChatRead, onPreviewUpdate };

  // Maior id de banco já conhecido (cursor do polling incremental)
  const maxDbId = (list) => list.reduce((max, m) => {
    const id = Number(m.id);
    return id > max ? id : max;
  }, 0);

  // Mescla mensagens novas: substitui pelo messageId quando já existe
  // (ex.: mensagem otimista que o servidor devolveu persistida), senão anexa
  const mergeIncoming = (prev, incoming) => {
    if (!incoming.length) return prev;
    const byMessageId = new Map(prev.map((m, index) => [m.messageId, index]));
    const next = [...prev];
    for (const msg of incoming) {
      const existingIndex = byMessageId.get(msg.messageId);
      if (existingIndex !== undefined) {
        next[existingIndex] = { ...next[existingIndex], ...msg, sendStatus: undefined };
      } else {
        next.push(msg);
      }
    }
    return next;
  };

  // Carga inicial + reset ao trocar de conversa
  useEffect(() => {
    setMessages([]);
    setHasMore(false);

    if (!chatKey) return;

    let active = true;
    setLoading(true);

    apiRef.current.GetWhatsappChatMessages(chatKey, { limit: PAGE_SIZE }, (result) => {
      if (!active) return;
      setMessages(result?.data || []);
      setHasMore(!!result?.hasMore);
      setLoading(false);

      // Marca como lida no servidor e zera o contador local
      apiRef.current.SetWhatsappChatRead(chatKey);
      if (apiRef.current.onChatRead) apiRef.current.onChatRead(chatKey);
    });

    return () => { active = false; };
  }, [chatKey]);

  // Polling incremental do chat aberto
  useEffect(() => {
    if (!chatKey) return;

    const intervalId = setInterval(() => {
      const sinceId = maxDbId(messagesRef.current);
      if (sinceId === 0) return; // carga inicial ainda não terminou

      apiRef.current.GetWhatsappChatMessages(chatKey, { sinceId }, (result) => {
        const incoming = result?.data || [];
        if (!incoming.length) return;

        setMessages((prev) => mergeIncoming(prev, incoming));

        // Chegou mensagem do paciente com o chat aberto: já marca como lida
        const hasReceived = incoming.some((m) => m.direction === 'received');
        if (hasReceived) {
          apiRef.current.SetWhatsappChatRead(chatKey);
          if (apiRef.current.onChatRead) apiRef.current.onChatRead(chatKey);
        }

        // Atualiza a prévia na lista sem esperar o polling da lista
        const last = incoming[incoming.length - 1];
        if (apiRef.current.onPreviewUpdate) {
          apiRef.current.onPreviewUpdate(chatKey, {
            messageText: last.messageText,
            messageType: last.messageType,
            caption: last.caption,
            fileName: last.fileName,
            date: last.date,
          });
        }
      });
    }, POLL_INTERVAL);

    return () => clearInterval(intervalId);
  }, [chatKey]);

  // Página mais antiga (scroll infinito para cima)
  const loadOlder = useCallback(() => {
    if (!chatKey || loadingOlder || !hasMore) return;

    const withDbId = messagesRef.current.filter((m) => Number(m.id) > 0);
    if (!withDbId.length) return;
    const beforeId = Number(withDbId[0].id);

    setLoadingOlder(true);
    apiRef.current.GetWhatsappChatMessages(chatKey, { beforeId, limit: PAGE_SIZE }, (result) => {
      const older = result?.data || [];
      setMessages((prev) => {
        const known = new Set(prev.map((m) => m.messageId));
        return [...older.filter((m) => !known.has(m.messageId)), ...prev];
      });
      setHasMore(!!result?.hasMore);
      setLoadingOlder(false);
    });
  }, [chatKey, loadingOlder, hasMore]);

  // Dispara o envio de uma mensagem já presente na lista (nova ou retry).
  // O destinatário é o sendTarget (telefone real quando conhecido, senão o
  // próprio lid) — nunca a chave da conversa.
  const dispatchSend = useCallback((tempId, text) => {
    apiRef.current.SendWhatsappMessage(sendTargetRef.current, text, 0, (result) => {
      if (result && result.success) {
        setMessages((prev) => prev.map((m) =>
          m.messageId === tempId
            ? { ...m, messageId: result.messageId || tempId, sendStatus: 'sent' }
            : m
        ));
      } else {
        setMessages((prev) => prev.map((m) =>
          m.messageId === tempId ? { ...m, sendStatus: 'failed' } : m
        ));
      }
    });
  }, [chatKey]);

  const sendMessage = useCallback((text) => {
    const trimmed = (text || '').trim();
    if (!trimmed || !chatKey) return;

    const tempId = 'temp_' + Date.now() + '_' + Math.floor(Math.random() * 1000);
    const optimistic = {
      messageId: tempId,
      messageText: trimmed,
      messageType: 'text',
      direction: 'sent',
      senderPhone: sendTargetRef.current,
      date: new Date().toISOString(),
      sendStatus: 'sending',
    };

    setMessages((prev) => [...prev, optimistic]);
    dispatchSend(tempId, trimmed);

    if (apiRef.current.onPreviewUpdate) {
      apiRef.current.onPreviewUpdate(chatKey, {
        messageText: trimmed,
        messageType: 'text',
        caption: null,
        date: optimistic.date,
      });
    }
  }, [chatKey, dispatchSend]);

  const retryMessage = useCallback((messageId) => {
    const msg = messagesRef.current.find((m) => m.messageId === messageId);
    if (!msg || msg.sendStatus !== 'failed') return;

    setMessages((prev) => prev.map((m) =>
      m.messageId === messageId ? { ...m, sendStatus: 'sending' } : m
    ));
    dispatchSend(messageId, msg.messageText);
  }, [dispatchSend]);

  const discardMessage = useCallback((messageId) => {
    setMessages((prev) => prev.filter((m) =>
      !(m.messageId === messageId && m.sendStatus === 'failed')
    ));
  }, []);

  return {
    messages,
    loading,
    loadingOlder,
    hasMore,
    loadOlder,
    sendMessage,
    retryMessage,
    discardMessage,
  };
}
